import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/feature/SEOHead';
import { testimonials, siteStats, aboutContent } from '@/mocks/manhwa';
import { useState, FormEvent } from 'react';

export default function AboutPage() {
  const [formData, setFormData] = useState({ name: '', email: '', company: '', message: '' });
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    setFormError('');

    const form = e.currentTarget;
    const honeypot = (form.elements.namedItem('website_alt') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormStatus('success');
      setFormData({ name: '', email: '', company: '', message: '' });
      setSubmitting(false);
      setTimeout(() => setFormStatus('idle'), 3000);
      return;
    }

    try {
      const payload = new FormData(form);
      const response = await fetch('http://911hl.cn/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(payload as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormData({ name: '', email: '', company: '', message: '' });
        setTimeout(() => setFormStatus('idle'), 5000);
      } else {
        setFormStatus('error');
        setFormError(parsed?.meta?.message || parsed?.message || '提交失败，请稍后重试。');
        setTimeout(() => setFormStatus('idle'), 5000);
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请稍后重试。');
      setTimeout(() => setFormStatus('idle'), 5000);
    } finally {
      setSubmitting(false);
    }
  };

  const breadcrumbItems = [{ label: '首页', path: '/' }, { label: '关于我们' }];

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '韩漫星球',
    url: 'http://911hl.cn/',
    description: '国内领先的韩国漫画在线免费阅读平台',
    email: aboutContent.contactEmail,
    telephone: aboutContent.phone,
    address: {
      '@type': 'PostalAddress',
      addressLocality: '北京',
      addressRegion: '朝阳区',
      streetAddress: '望京SOHO T3-A座 2508室',
    },
  };

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于韩漫星球',
    description: '韩漫星球是国内领先的韩国漫画在线免费阅读平台，提供最全面的韩漫资源。',
    url: 'http://911hl.cn/about',
    inLanguage: 'zh-CN',
  };

  return (
    <>
      <SEOHead
        title="关于我们 - 韩漫星球 | 韩国漫画免费在线阅读平台"
        description="韩漫星球是国内领先的韩国漫画在线免费阅读平台，收录超过3200部韩国漫画，全部免费阅读。了解我们的品牌故事、平台实力与商务合作信息。"
        keywords="韩漫星球,关于我们,韩国漫画平台,韩漫网站,商务合作,联系我们"
        canonicalPath="/about"
        schemaJson={[orgSchema, aboutSchema]}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main>
          <div className="pt-20 pb-10 md:pb-14 px-4 md:px-6">
            <div className="max-w-7xl mx-auto">
              <div className="mb-4">
                <Breadcrumb items={breadcrumbItems} />
              </div>

              <div className="flex flex-col lg:flex-row gap-8 md:gap-5 mb-14 md:mb-20">
                <div className="lg:w-[45%]">
                  <h1 className="text-2xl md:text-4xl lg:text-5xl font-bold font-heading text-foreground-950 leading-tight mb-4">
                    让每一位读者都能
                    <em className="text-primary-500 not-italic">免费阅读</em>
                    优质韩漫
                  </h1>
                  <p className="text-foreground-700 text-sm md:text-base leading-relaxed mb-6">
                    {aboutContent.brandIntro}
                  </p>
                  <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                    <a href="#contact-form" className="whitespace-nowrap bg-primary-500 hover:bg-primary-600 text-background-50 px-8 py-3 rounded-full text-sm font-medium cursor-pointer transition-colors inline-flex items-center gap-2">
                      <i className="ri-group-line"></i>
                      商务合作
                    </a>
                    <a href="#brand-story" className="whitespace-nowrap border border-background-300 text-foreground-700 hover:bg-background-100 px-8 py-3 rounded-full text-sm font-medium cursor-pointer transition-colors inline-flex items-center gap-2">
                      <i className="ri-information-line"></i>
                      了解更多
                    </a>
                  </div>
                </div>
                <div className="lg:w-[55%]">
                  <img
                    src="https://readdy.ai/api/search-image?query=Korean%20webtoon%20studio%20creative%20workspace%20with%20digital%20drawing%20tablets%2C%20warm%20atmospheric%20lighting%2C%20modern%20minimal%20office%20design%2C%20comic%20art%20on%20screens%2C%20professional%20creative%20environment%2C%20editorial%20photography%20style%2C%20clean%20composition%2C%20soft%20natural%20light&width=900&height=600&seq=about-hero-img&orientation=landscape"
                    alt="韩漫星球团队"
                    className="w-full rounded-xl object-cover"
                    title="韩漫星球 - 关于我们"
                  />
                </div>
              </div>

              <section id="brand-story" className="mb-14 md:mb-20">
                <div className="flex flex-col lg:flex-row gap-8">
                  <div className="lg:w-1/3">
                    <span className="inline-block px-4 py-1.5 rounded-full border border-primary-300 text-primary-600 text-sm mb-4 whitespace-nowrap">
                      品牌故事
                    </span>
                  </div>
                  <div className="lg:w-2/3">
                    <p className="text-foreground-700 text-sm md:text-base leading-relaxed mb-8">
                      {aboutContent.brandStory}
                    </p>

                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 md:gap-5">
                      <div className="bg-background-100 p-6 rounded-lg text-center">
                        <p className="text-3xl md:text-4xl font-bold text-foreground-950 mb-2">850万<strong className="text-primary-500">+</strong></p>
                        <p className="text-xs text-foreground-600">累计用户</p>
                      </div>
                      <div className="bg-background-100 p-6 rounded-lg text-center">
                        <p className="text-3xl md:text-4xl font-bold text-foreground-950 mb-2">3200<strong className="text-primary-500">+</strong></p>
                        <p className="text-xs text-foreground-600">收录韩漫</p>
                      </div>
                      <div className="bg-background-100 p-6 rounded-lg text-center">
                        <p className="text-3xl md:text-4xl font-bold text-foreground-950 mb-2">{siteStats.dailyActiveGrowth}</p>
                        <p className="text-xs text-foreground-600">日活增长率</p>
                      </div>
                      <div className="bg-background-100 p-6 rounded-lg text-center">
                        <p className="text-3xl md:text-4xl font-bold text-foreground-950 mb-2">{siteStats.partnerPlatforms}<strong className="text-primary-500">+</strong></p>
                        <p className="text-xs text-foreground-600">合作平台</p>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              <section className="mb-14 md:mb-20">
                <div className="text-center mb-8">
                  <div className="flex items-center justify-center gap-2 mb-4">
                    <span className="w-2 h-2 rounded-full bg-primary-500"></span>
                    <span className="text-sm text-primary-600 font-medium">口碑背书</span>
                  </div>
                  <h2 className="text-2xl md:text-4xl font-bold font-heading text-foreground-950 mb-4">
                    创作者与读者的选择
                  </h2>
                  <p className="text-foreground-600 text-sm md:text-base max-w-xl mx-auto">
                    来自创作者和读者的真实声音
                  </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-5">
                  {testimonials.slice(0, 3).map((t, i) => (
                    <div
                      key={t.id}
                      className="bg-background-100 p-6 rounded-lg"
                    >
                      <div className="flex items-center gap-1 mb-3">
                        {Array.from({ length: t.rating }).map((_, idx) => (
                          <i key={idx} className="ri-star-fill text-accent-500 text-sm"></i>
                        ))}
                      </div>
                      <h4 className="text-sm font-semibold text-foreground-950 mb-2">{t.title}</h4>
                      <p className="text-sm text-foreground-600 leading-relaxed mb-4">{t.content}</p>
                      <div className="flex items-center gap-3">
                        <img src={t.avatar} alt={t.name} className="w-10 h-10 rounded-full object-cover" />
                        <div>
                          <p className="text-sm font-semibold text-foreground-950">{t.name}</p>
                          <p className="text-xs text-foreground-500">{t.role}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              <section className="mb-14 md:mb-20" id="contact-form">
                <div className="max-w-2xl mx-auto">
                  <div className="text-center mb-8">
                    <span className="inline-block px-4 py-1.5 rounded-full border border-primary-300 text-primary-600 text-sm mb-4 whitespace-nowrap">
                      联系我们
                    </span>
                    <h2 className="text-2xl md:text-4xl font-bold font-heading text-foreground-950 mb-4">
                      商务合作咨询
                    </h2>
                    <p className="text-foreground-600 text-sm md:text-base">
                      无论您是漫画作者、出版社还是广告合作方，欢迎与我们联系
                    </p>
                  </div>

                  <div className="bg-background-100 p-6 md:p-5 rounded-xl">
                    <form onSubmit={handleSubmit} id="d96tt5vpg5pqhofcut30">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                        <div>
                          <label className="block text-sm font-medium text-foreground-700 mb-1.5">姓名</label>
                          <input
                            type="text"
                            name="name"
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200/70 rounded-lg focus:outline-none focus:border-primary-400 text-foreground-800"
                            required
                            placeholder="您的姓名"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-foreground-700 mb-1.5">邮箱</label>
                          <input
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                            className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200/70 rounded-lg focus:outline-none focus:border-primary-400 text-foreground-800"
                            required
                            placeholder="hello@911hl.cn"
                          />
                        </div>
                      </div>
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-foreground-700 mb-1.5">公司/机构</label>
                        <input
                          type="text"
                          name="company"
                          value={formData.company}
                          onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                          className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200/70 rounded-lg focus:outline-none focus:border-primary-400 text-foreground-800"
                          placeholder="您的公司或机构名称"
                        />
                      </div>
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-foreground-700 mb-1.5">合作需求</label>
                        <textarea
                          name="message"
                          value={formData.message}
                          onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                          rows={4}
                          maxLength={500}
                          className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200/70 rounded-lg focus:outline-none focus:border-primary-400 text-foreground-800 resize-none"
                          required
                          placeholder="请描述您的合作需求（500字以内）"
                        ></textarea>
                        <p className="text-xs text-foreground-400 mt-1">{formData.message.length}/500</p>
                      </div>

                      <input
                        type="text"
                        name="website_alt"
                        className="honeypot-field"
                        tabIndex={-1}
                        autoComplete="off"
                        aria-hidden="true"
                        readOnly
                      />

                      <button
                        type="submit"
                        disabled={submitting}
                        className="whitespace-nowrap w-full bg-primary-500 hover:bg-primary-600 disabled:bg-primary-300 text-background-50 py-3 rounded-lg text-sm font-medium cursor-pointer transition-colors"
                      >
                        {submitting ? '提交中...' : '提交合作需求'}
                      </button>

                      {formStatus === 'success' && (
                        <p className="text-sm text-accent-600 mt-3 text-center">提交成功！我们会尽快与您联系。</p>
                      )}
                      {formStatus === 'error' && (
                        <p className="text-sm text-red-500 mt-3 text-center">{formError}</p>
                      )}
                    </form>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-8">
                    <div className="text-center p-4">
                      <i className="ri-mail-line text-2xl text-primary-500 mb-2 block"></i>
                      <p className="text-xs text-foreground-500 mb-1">商务合作</p>
                      <p className="text-sm font-medium text-foreground-800">{aboutContent.businessEmail}</p>
                    </div>
                    <div className="text-center p-4">
                      <i className="ri-phone-line text-2xl text-primary-500 mb-2 block"></i>
                      <p className="text-xs text-foreground-500 mb-1">联系电话</p>
                      <p className="text-sm font-medium text-foreground-800">{aboutContent.phone}</p>
                    </div>
                    <div className="text-center p-4">
                      <i className="ri-map-pin-line text-2xl text-primary-500 mb-2 block"></i>
                      <p className="text-xs text-foreground-500 mb-1">公司地址</p>
                      <p className="text-sm font-medium text-foreground-800">{aboutContent.address}</p>
                    </div>
                  </div>
                </div>
              </section>
            </div>
          </div>

          <section className="relative w-full min-h-[400px] md:min-h-[450px] flex items-center overflow-hidden">
            <div className="absolute inset-0">
              <img
                src="https://readdy.ai/api/search-image?query=Korean%20manhwa%20webtoon%20art%20panels%20abstract%20collage%20background%2C%20warm%20vibrant%20illustration%2C%20digital%20art%20style%2C%20soft%20gradient%2C%20creative%20atmosphere%2C%20editorial%20quality%2C%20webtoon%20aesthetic%2C%20no%20readable%20text&width=1920&height=600&seq=about-cta-bg&orientation=landscape"
                alt="韩漫合作背景"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/70 via-foreground-950/40 to-foreground-950/20"></div>
            </div>
            <div className="relative z-10 w-full px-4 md:px-6">
              <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                <div>
                  <h2 className="text-3xl md:text-5xl font-bold font-heading text-background-50 leading-tight mb-6">
                    与韩漫星球
                    <br />
                    一起成长
                  </h2>
                  <a href="#contact-form" className="whitespace-nowrap inline-flex items-center gap-2 bg-background-50 text-foreground-950 hover:bg-background-100 px-8 py-3 rounded-full text-sm font-medium cursor-pointer transition-colors">
                    联系我们
                    <span className="w-6 h-6 flex items-center justify-center bg-foreground-950 rounded-full">
                      <i className="ri-arrow-right-line text-background-50 text-xs"></i>
                    </span>
                  </a>
                </div>
                <div className="text-right">
                  <p className="text-background-50/70 text-sm leading-relaxed max-w-sm ml-auto">
                    漫画作者 · 出版社 · 广告合作<br />
                    欢迎所有形式的商务合作洽谈
                  </p>
                </div>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}